/* eslint-disable no-console */
import { useUserInfo } from '@react/User/useUserInfo'
import { useEffect, useMemo, useState } from 'react'

interface ProductMapping {
  productCategory: string
  mappedValue: string
  hepAccessGroup: string
  'product-tags': string[]
  'data-path': string
}

interface EventItem {
  title?: string
  tags?: string[]
  powerportalTags?: string[]
  country?: string
  targetGeographyTags?: string[]
  utcEventStartDate?: number
  [key: string]: unknown
}

interface UsePowerPortalEventsProps {
  items: EventItem[]
}

// Helper function to check category match
const hasCategoryMatch = (tags: string[], categories: string[]): boolean => {
  return tags.some((tag: string) =>
    categories.some(category => tag.includes(category))
  )
}

// Helper function to check global geography
const isGlobalGeography = (targetGeography: string[]): boolean => {
  return targetGeography.some((tag: string) => tag.toLowerCase().includes('global'))
}

export const usePowerPortalEvents = ({ items }: UsePowerPortalEventsProps) => {
  // Check if we're in PowerPortal context - only execute if cmp-powerportal-main is present in body
  const isPowerPortal = typeof document !== 'undefined' && document.body.classList.contains('cmp-powerportal-main')
  
  const { userDetails } = useUserInfo()
  const [productMapping, setProductMapping] = useState<Record<string, ProductMapping> | null>(null)

  // Fetch product mapping ONLY if in PowerPortal
  useEffect(() => {
    if (!isPowerPortal) return

    const fetchProductMapping = async () => {
      try {
        const apiUrl = `/bin/pg/powerportal/personalization-mapping.json`
        const response = await fetch(apiUrl, { credentials: 'same-origin' })
        if (!response.ok) throw new Error(`Failed with status`)
        const data = await response.json()
        setProductMapping(data)
       
      } catch (error) {
        console.error('PowerPortalEvents - Error loading mapping:', error)
      }
    }
    fetchProductMapping()
  }, [isPowerPortal])

  // Memoize sorted items to prevent unnecessary re-renders
  const sortedItems = useMemo(() => {
    // If not in PowerPortal, return original items
    if (!isPowerPortal) {
      return items
    }

    // If no product mapping yet, return original items
    if (!productMapping || items.length === 0) {
      return items
    }

    const productPreference = userDetails?.powerPortalProfile?.samlAttributes?.partnerProgramPreference as string | undefined
    const countryName = userDetails?.powerPortalProfile?.samlAttributes?.countryName as string | undefined

    if (!productPreference) {
      return items
    }

    // Get matched categories from partnerProgramPreference
    const preferenceCodes = productPreference.split(',').map(code => code.trim())
   
    const matchedProducts = Object.entries(productMapping)
      .filter(([code]) => preferenceCodes.includes(code))
      .map(([code, product]) => ({ code, ...product }))

    const matchedCategories: string[] = []
    matchedProducts.forEach((product) => {
      if (product.productCategory && !matchedCategories.includes(product.productCategory)) {
        matchedCategories.push(product.productCategory)
      }
    })

    if (matchedCategories.length === 0) {
      return items
    }

    // Group items by priority levels
    // Priority 1: Matches user's category AND country
    const categoryAndCountryMatch: EventItem[] = []
    // Priority 2: Matches user's category AND has global geography tag
    const categoryAndGlobalMatch: EventItem[] = []
    // Priority 3: Matches user's category only
    const categoryOnlyMatch: EventItem[] = []
    // Priority 4: No category match, sorted by latest date
    const noMatch: EventItem[] = []

    items.forEach((item) => {
      const tags = item.powerportalTags || item.tags || []
      const itemCountry = item.country || ''
      const targetGeography = item.targetGeographyTags || []

      const itemHasCategoryMatch = hasCategoryMatch(tags, matchedCategories)
      const itemHasCountryMatch = countryName && itemCountry.toLowerCase() === countryName.toLowerCase()
      const itemIsGlobal = isGlobalGeography(targetGeography)

      if (itemHasCategoryMatch && itemHasCountryMatch) {
        categoryAndCountryMatch.push(item)
      } else if (itemHasCategoryMatch && itemIsGlobal) {
        categoryAndGlobalMatch.push(item)
      } else if (itemHasCategoryMatch) {
        categoryOnlyMatch.push(item)
      } else {
        noMatch.push(item)
      }
    })

    // Sort the "no match" items by latest date
    noMatch.sort((a, b) => {
      const aStartDate = a.utcEventStartDate || 0
      const bStartDate = b.utcEventStartDate || 0
      return bStartDate - aStartDate
    })

    // Combine all priority groups in order
    return [
      ...categoryAndCountryMatch,
      ...categoryAndGlobalMatch,
      ...categoryOnlyMatch,
      ...noMatch
    ]
  }, [items, productMapping, userDetails?.powerPortalProfile?.samlAttributes?.partnerProgramPreference, userDetails?.powerPortalProfile?.samlAttributes?.countryName, isPowerPortal])

  return { sortedItems }
}
